import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import Carousel from 'LuxKit/Carousel/Carousel'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineCoffeeIcon from 'LuxKit/Icons/line/LineCoffeeIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import { trackClientEvent } from 'analytics/analytics'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'

interface Props {
  breakfastIncluded: boolean
  freeCancellation: boolean
}

function toggleStateToClientEventAction(toggleState: boolean): 'toggled_on' | 'toggled_off' {
  return toggleState ? 'toggled_on' : 'toggled_off'
}

function PropertyQuickFilters({ breakfastIncluded, freeCancellation }: Props) {
  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()

  const breakfastIncludedLabel = intl.formatMessage({
    id: 'w2jEyEHf_MDWoniUCBnT',
    defaultMessage: 'Breakfast included',
    description: 'Label for togglable filter chip that restricts displayed hotel rates to those with breakfast included',
  })
  const freeCancellationLabel = intl.formatMessage({
    id: 'Cnk7NVK8VWdi1ubUWdxY',
    defaultMessage: 'Free cancellation',
    description: 'Label for togglable filter chip that restricts displayed hotel rates to those with free cancellation',
  })

  const toggleBreakfastIncluded = useCallback(() => {
    const newToggleState = !breakfastIncluded
    offerPageDispatch({
      type: OfferPageActions.setQuickFilters,
      quickFilters: { breakfastIncluded: newToggleState },
    })
    trackClientEvent({
      subject: 'breakfast_included_quick_filter',
      action: toggleStateToClientEventAction(newToggleState),
      category: 'offer_page',
      type: 'interaction',
    })
  }, [breakfastIncluded, offerPageDispatch])
  const toggleFreeCancellation = useCallback(() => {
    const newToggleState = !freeCancellation
    offerPageDispatch({
      type: OfferPageActions.setQuickFilters,
      quickFilters: { freeCancellation: newToggleState },
    })
    trackClientEvent({
      subject: 'free_cancellation_quick_filter',
      action: toggleStateToClientEventAction(newToggleState),
      category: 'offer_page',
      type: 'interaction',
    })
  }, [freeCancellation, offerPageDispatch])

  return <Carousel gutterStyle="overflow" gap={8}>
    <FilterChip
      size="medium"
      selected={breakfastIncluded}
      startIcon={breakfastIncluded ? <LineCheckIcon /> : <LineCoffeeIcon />}
      onClick={toggleBreakfastIncluded}
    >
      {breakfastIncludedLabel}
    </FilterChip>
    <FilterChip
      size="medium"
      selected={freeCancellation}
      startIcon={freeCancellation ? <LineCheckIcon /> : <LineShieldCheckIcon />}
      onClick={toggleFreeCancellation}
    >
      {freeCancellationLabel}
    </FilterChip>
  </Carousel>
}

type MappedProps = Props

export default withOfferPageState((state): MappedProps => ({
  breakfastIncluded: !!state.quickFilters?.breakfastIncluded,
  freeCancellation: !!state.quickFilters?.freeCancellation,
}), PropertyQuickFilters)
